import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, CalendarDays, MapPin, ChevronRight, Ticket, Hourglass } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const SESSION_KEY = 'gs_upcoming_event_pill_dismissed';

/**
 * Small glass "pill" popup (same style as the Gujarati greeting) that shows the next
 * upcoming public event. Appears just after the greeting minimises, sits directly
 * underneath it, links to the event page and can be dismissed for the session.
 * Hidden on staff-portal routes. Demo/training events are never returned publicly.
 */
export default function UpcomingEventPill() {
  const [event, setEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(SESSION_KEY) === '1');
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Staff choose the featured event in Admin → Events (or leave it automatic = next event; or switch it off)
    fetch(`${BACKEND_URL}/api/events/popup`)
      .then(r => r.json())
      .then(d => setEvent(d.event && d.event.status === 'active' ? d.event : null))
      .catch(() => setEvent(null));
    // Appear right after the greeting has minimised (greeting minimises at 2.5s)
    const t = setTimeout(() => setVisible(true), 3200);
    return () => clearTimeout(t);
  }, []);

  if (!event || dismissed || !visible) return null;
  // Never overlay the membership claim / verification forms - the fixed pill was intercepting taps on the form fields on phones.
  if (pathname.startsWith('/admin') || pathname.startsWith('/claim') || pathname.startsWith('/verify') || pathname.startsWith(`/events/${event.event_id}`)) return null;

  const days = Array.isArray(event.event_days) && event.event_days.length > 1 ? [...event.event_days].sort() : null;
  // Multi-day events: the first listed day is the real start (the headline "date" may be a later day)
  const start = days ? new Date(days[0] + (event.date && event.date.includes('T') ? event.date.slice(10) : 'T12:00:00')) : (event.date ? new Date(event.date) : null);
  const fmt = (d) => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  let when = start && !isNaN(start) ? fmt(start) : '';
  if (days) {
    const last = new Date(days[days.length - 1]);
    if (!isNaN(last)) when = `${fmt(start)} – ${fmt(last)}`;
  } else if (start && !isNaN(start) && event.date.includes('T')) {
    when += ` · ${start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  }

  // Countdown: "Starts in 12 days" / "Starts tomorrow" / "Today" / "Happening now" (multi-day events)
  const now = new Date();
  const startDay = start && !isNaN(start) ? new Date(start.getFullYear(), start.getMonth(), start.getDate()) : null;
  const lastDay = days ? new Date(days[days.length - 1] + 'T23:59:59') : (start && !isNaN(start) ? new Date(start.getFullYear(), start.getMonth(), start.getDate(), 23, 59, 59) : null);
  let countdown = '';
  if (startDay) {
    const diffDays = Math.ceil((startDay - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
    if (diffDays > 1) countdown = `Starts in ${diffDays} days`;
    else if (diffDays === 1) countdown = 'Starts tomorrow';
    else if (diffDays === 0) countdown = 'Today';
    else if (lastDay && now <= lastDay) countdown = 'Happening now';
  }
  const ticketsAvailable = event.status === 'active' && (!event.capacity || Number(event.tickets_sold || 0) < Number(event.capacity));

  const handleDismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem(SESSION_KEY, '1');
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: -100, scale: 0.8 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: -100, scale: 0.8 }}
        transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
        className="relative group pointer-events-auto max-w-[calc(100vw-2rem)] sm:max-w-sm"
        data-testid="upcoming-event-pill"
      >
        <div className="relative">
          <button
            type="button"
            onClick={() => navigate(`/events/${event.event_id}`)}
            className="text-left w-full bg-[hsl(var(--gs-navy))]/90 backdrop-blur-lg border border-white/25 rounded-2xl pl-4 pr-9 py-2.5 shadow-2xl hover:bg-[hsl(var(--gs-navy))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lux-gold))] transition-colors"
            style={{ backdropFilter: 'blur(16px) saturate(180%)', WebkitBackdropFilter: 'blur(16px) saturate(180%)' }}
            aria-label={`Upcoming event: ${event.title}. View details`}
            data-testid="upcoming-event-pill-link"
          >
            <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[#f5c451] font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              Upcoming event
              {countdown && (
                <span className="inline-flex items-center gap-1 normal-case tracking-normal rounded-full bg-white/90 text-[hsl(var(--gs-navy))] px-2 py-0.5 text-[10px] sm:text-[11px] font-bold" data-testid="upcoming-event-pill-countdown">
                  <Hourglass className="w-3 h-3" aria-hidden="true" />{countdown}
                </span>
              )}
            </p>
            <p className="text-white font-semibold text-sm sm:text-base leading-tight drop-shadow-md truncate" style={{ fontFamily: 'Space Grotesk' }} data-testid="upcoming-event-pill-title">
              {event.title}
            </p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-white/90 text-xs sm:text-sm mt-0.5">
              {when && <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" aria-hidden="true" />{when}</span>}
              {event.venue && <span className="inline-flex items-center gap-1 truncate max-w-[14rem]"><MapPin className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />{event.venue}</span>}
            </div>
            <ChevronRight className="w-4 h-4 text-white/80 absolute right-3 top-1/2 -translate-y-1/2 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
          </button>

          {ticketsAvailable && (
            <button
              type="button"
              onClick={() => navigate(`/events/${event.event_id}#tickets`)}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[hsl(var(--lux-gold))] text-[hsl(var(--gs-navy))] font-bold text-sm h-10 shadow-lg hover:bg-[hsl(var(--lux-gold-2,var(--lux-gold)))] hover:brightness-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-white transition-[transform,filter] active:scale-[0.98]"
              style={{ fontFamily: 'Space Grotesk' }}
              data-testid="upcoming-event-pill-buy-tickets"
            >
              <Ticket className="w-4 h-4" aria-hidden="true" /> Buy tickets
            </button>
          )}

          <button
            onClick={handleDismiss}
            className="absolute -top-2 right-0 sm:-right-2 w-6 h-6 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2"
            aria-label="Dismiss upcoming event notice"
            data-testid="upcoming-event-pill-dismiss"
          >
            <X className="w-3.5 h-3.5 text-[hsl(var(--gs-navy))]" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
